"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProjectLifecycle() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: stepsRef,
    isVisible: stepsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const steps = [{
    number: "01",
    title: "Weekly Workshops",
    description: "Join our collaborative sessions where we break down complex algorithms and build real-world applications together.",
    detail: "Every Tuesday at 7 PM",
    mascot: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/hackathon_coding_setup.png",
    mascotAlt: "High-contrast photo of a collaborative hackathon coding setup"
  }, {
    number: "02",
    title: "Discord Channels",
    description: "Connect with fellow builders, share your latest commits, and get instant feedback on your passion projects.",
    detail: "24/7 community access",
    mascot: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_passion_project.png",
    mascotAlt: "Close up of a member passion project on a mechanical keyboard"
  }, {
    number: "03",
    title: "Build Nights",
    description: "Dedicated time to focus on your own hardware or software builds in a supportive, high-energy environment.",
    detail: "Monthly open-source toolkit",
    mascot: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/hardware_setup_lab.webp",
    mascotAlt: "Sleek hardware setup with glowing LED components"
  }];
  return <section data-section-id="1075" id="project-lifecycle" className="bg-background text-foreground relative overflow-hidden py-28 md:py-36">
      {}
      <svg className="absolute top-0 left-0 w-full h-6 text-background" viewBox="0 0 1440 24" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0,8 C120,2 200,16 320,10 C440,4 560,18 680,12 C800,6 920,20 1040,14 C1160,8 1280,18 1440,10 L1440,0 L0,0 Z" fill="currentColor" />
      </svg>

      {}
      <div className="absolute top-40 -left-32 w-[480px] h-[480px] rounded-lg opacity-30 blur-3xl pointer-events-none" style={{
      backgroundColor: "hsl(270 100% 64% / 0.15)"
    }} aria-hidden="true" />
      <div className="absolute bottom-20 -right-40 w-[560px] h-[560px] rounded-lg opacity-25 blur-3xl pointer-events-none" style={{
      backgroundColor: "hsl(240 10% 20% / 0.35)"
    }} aria-hidden="true" />

      <div className="relative max-w-[1320px] mx-auto px-5 md:px-8 lg:px-12">
        {}
        <div ref={headerRef} className={`max-w-[720px] mx-auto text-center transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="font-mono text-xs md:text-sm uppercase mb-6" style={{
          color: "var(--primary)",
          letterSpacing: "0.18em"
        }}><span aria-hidden="true" data-ast-id="f:/src/pages/TheLab/sections/ProjectLifecycle.tsx|sig:hksxgc" data-ast-start="3365" data-ast-end="3397"></span>Club Workflow</div>
          <h2 className="font-default font-semibold text-foreground leading-[1.05]" style={{
          fontSize: "clamp(2rem, 4.2vw, 3.75rem)",
          letterSpacing: "-0.02em"
        }}>
            Command center for the{" "}
            <em className="font-default inline-block" style={{
            color: "var(--primary)",
            fontStyle: "normal"
          }}>
              next generation
            </em>
          </h2>
          <p className="font-default mt-6 text-muted-foreground leading-relaxed" style={{
          fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)"
        }}>
            Code Hub provides the infrastructure, community, and tools you need to turn your technical ideas into reality.
          </p>
        </div>

        {}
        <div ref={stepsRef} className="relative mt-20 md:mt-24">
          {}
          <svg className="hidden md:block absolute top-[120px] left-0 w-full pointer-events-none" height="80" viewBox="0 0 1200 80" preserveAspectRatio="none" aria-hidden="true">
            <path id="forest-path" d="M 200,40 Q 350,10 500,40 T 800,40 Q 950,70 1000,40" fill="none" stroke="var(--border)" strokeWidth="2" strokeDasharray="2 8" strokeLinecap="round" opacity="0.6" />
            {}
            <g style={{
            color: "var(--primary)"
          }}>
              <circle r="5" fill="currentColor">
                <animateMotion dur="8s" repeatCount="indefinite" keyTimes="0;0.12;0.13;0.5;0.51;0.87;0.88;1" keyPoints="0;0.5;0.5;0.5;1;1;0;0" calcMode="linear">
                  <mpath href="#forest-path" />
                </animateMotion>
              </circle>
            </g>
          </svg>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-8 relative">
            {steps.map((step, i) => <div data-index={i} key={i} className={`relative transition-all duration-700 ease-out ${stepsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                {}
                {i < steps.length - 1 && <div className="md:hidden absolute left-1/2 -bottom-8 -translate-x-1/2 h-8 w-px" style={{
              backgroundImage: "radial-gradient(circle, var(--border) 1.5px, transparent 1.5px)",
              backgroundSize: "4px 8px",
              backgroundRepeat: "repeat-y",
              opacity: 0.6
            }} aria-hidden="true" />}

                <div className="relative rounded-lg p-8 h-full" style={{
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)"
            }}>
                  {}
                  <img src={step.mascot} data-wvc-srcset={`${step.mascot}-wvc-srcset`} data-wvc-sizes={`${step.mascot}-wvc-sizes`} alt={step.mascotAlt} loading="lazy" decoding="async" className="absolute top-3 right-3 w-[88px] h-[88px] md:w-[100px] md:h-[100px] object-cover rounded-md pointer-events-none" />

                  {}
                  <div className="font-mono font-bold leading-none mb-6" style={{
                fontSize: "clamp(3rem, 5vw, 4.5rem)",
                color: "var(--border)",
                display: "inline-block"
              }}>
                    {step.number}
                  </div>

                  <h3 className="font-default font-semibold text-foreground mb-4 pr-20" style={{
                fontSize: "clamp(1.375rem, 2vw, 1.875rem)",
                letterSpacing: "-0.01em",
                lineHeight: "1.2"
              }}>
                    {step.title}
                  </h3>

                  <p className="font-default text-muted-foreground leading-relaxed mb-6" style={{
                fontSize: "1rem"
              }}>
                    {step.description}
                  </p>

                  {}
                  <div className="flex items-center gap-2.5 pt-5 border-t border-border">
                    <div className="w-2 h-2 rounded-lg bg-primary" />
                    <span className="font-mono text-foreground" style={{
                  fontSize: "0.85rem",
                  fontWeight: 500
                }}>
                      {step.detail}
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {}
        <div ref={ctaRef} className={`mt-20 md:mt-24 flex flex-col items-center transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button size="lg" className="rounded-lg px-8 py-6 text-base font-semibold hover:opacity-90 transition-opacity duration-200" style={{
          backgroundColor: "var(--primary)",
          color: "var(--primary-foreground)"
        }}>
            Download Open-Source Toolkit
            <ArrowRight className="ml-1 w-5 h-5" />
          </Button>
          <p className="font-default mt-4 text-muted-foreground" style={{
          fontSize: "0.875rem"
        }}>
            Free access for all community members
          </p>
        </div>
      </div>

      {}
      <svg className="absolute bottom-0 left-0 w-full h-6 text-background rotate-180" viewBox="0 0 1440 24" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0,8 C120,2 200,16 320,10 C440,4 560,18 680,12 C800,6 920,20 1040,14 C1160,8 1280,18 1440,10 L1440,0 L0,0 Z" fill="currentColor" />
      </svg>
    </section>;
}